import { z } from 'zod'

/**
 * The language models this tool can bill for, by id, with their display names.
 *
 * IDS AND LABELS ONLY. The prices live server-side, in `ai-usage/pricing.ts`:
 * a list price next to a token count is the purchase price, which is exactly
 * what must not reach the browser (see `ai-usage.ts`). A model that is not in
 * this list has no price, so it cannot be offered as a choice — billing and the
 * monthly limit would both be blind to it.
 *
 * The pricing table is typed against `PricedModelId`, so adding an id here
 * without a price fails the typecheck rather than the first invoice.
 */
export const ANTHROPIC_MODEL_IDS = [
  'claude-opus-5-5',
  'claude-sonnet-5-5',
  'claude-haiku-5-5',
] as const
export type AnthropicModelId = (typeof ANTHROPIC_MODEL_IDS)[number]

/**
 * Mistral's chat models for briefings, by their DATED ids (an alias like
 * `mistral-medium-latest` can move to a model with another price). Taken from
 * the account's `GET /v1/models` on 2026-10-20: Medium 3.5 (262k context,
 * reasoning), Large 4 (1M context, reasoning, public preview at a launch
 * discount) and Large 3 (262k, no reasoning, the cheap one).
 */
export const MISTRAL_MODEL_IDS = [
  'mistral-medium-2604',
  'mistral-large-4-0',
  'mistral-large-2512',
] as const
export type MistralModelId = (typeof MISTRAL_MODEL_IDS)[number]

/**
 * The OCR model the Mistral driver reads scanned pages with. Priced per PAGE,
 * not per token, so it is not a `PricedModelId`; the server's price table has
 * its own entry for it. The DATED id (OCR 4.1), not `mistral-ocr-latest` or
 * `mistral-ocr-4`: those aliases can move to a model with another price, and
 * the price table would then bill the new model at the old figure.
 */
export const MISTRAL_OCR_MODEL_ID = 'mistral-ocr-4-1'

export const PRICED_MODEL_IDS = [...ANTHROPIC_MODEL_IDS, ...MISTRAL_MODEL_IDS] as const

export type PricedModelId = (typeof PRICED_MODEL_IDS)[number]

export const MODEL_LABELS: Record<PricedModelId, string> = {
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5',
  'claude-haiku-5-5': 'Claude Haiku 5.5',
  'mistral-medium-2604': 'Mistral Medium 3.5',
  'mistral-large-4-0': 'Mistral Large 4',
  'mistral-large-2512': 'Mistral Large 3',
}

export function isPricedModelId(value: string): value is PricedModelId {
  return (PRICED_MODEL_IDS as readonly string[]).includes(value)
}

export function isAnthropicModelId(value: string): value is AnthropicModelId {
  return (ANTHROPIC_MODEL_IDS as readonly string[]).includes(value)
}

export function isMistralModelId(value: string): value is MistralModelId {
  return (MISTRAL_MODEL_IDS as readonly string[]).includes(value)
}

/* ------------------------------------------------------ briefing models --- */

/**
 * Who runs a briefing's model call. `anthropic` is the Messages API directly,
 * with native PDF input and a free token count; `openrouter` is the same
 * OpenRouter account the Bild-KI uses, which fronts a few hundred models behind
 * one OpenAI-style endpoint and REPORTS THE COST of every call — which is what
 * makes a model with no entry in the price table billable at all; `mistral`
 * is Mistral's EU regional endpoint (`api.eu.mistral.ai`), where the documents
 * stay inside the EU — the text layer is read locally and only pages without
 * one go through Mistral OCR before one text-only chat call.
 */
export const BRIEFING_PROVIDERS = ['anthropic', 'openrouter', 'mistral'] as const
export type BriefingProviderId = (typeof BRIEFING_PROVIDERS)[number]

export const BRIEFING_PROVIDER_LABELS: Record<BriefingProviderId, string> = {
  anthropic: 'Anthropic (direkt)',
  openrouter: 'OpenRouter',
  mistral: 'Mistral AI (EU)',
}

/**
 * An OpenRouter model id: `vendor/model`, optionally `:variant` (`:free`,
 * `:batch`). The format is checked here; whether the model EXISTS is checked
 * against OpenRouter's catalogue when an estimate or a run asks for it, so a
 * save never needs the network.
 */
export const OPENROUTER_MODEL_ID_PATTERN = /^[a-z0-9][a-z0-9_.-]*\/[a-z0-9][a-z0-9_.:-]*$/i

const OPENROUTER_MODEL_ID_HINT =
  'OpenRouter-Modelle heißen „anbieter/modell“, z. B. google/gemini-4-argon'

// Lower-cased on the way in: OpenRouter's ids are lower case, and a pasted
// "Google/Gemini-4-Argon" would pass the format and then miss the catalogue
// at the first estimate. Every message is German — the settings card prints
// the first issue verbatim.
export const openRouterModelIdSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, OPENROUTER_MODEL_ID_HINT)
  .max(120, 'Höchstens 120 Zeichen.')
  .regex(OPENROUTER_MODEL_ID_PATTERN, OPENROUTER_MODEL_ID_HINT)

/**
 * The briefing model as a setting: ONE value naming the provider and the id,
 * so that the two can never disagree. A setting is stored and validated per
 * LEAF (see the settings store), which rules out a cross-field rule between
 * two leaves — a `provider` leaf and a `model` leaf would each validate alone
 * and together describe a model Anthropic does not serve.
 *
 * The Anthropic branch stays an enum over the price table (D3): there the
 * server prices the tokens itself. The OpenRouter branch is free text in the
 * id's format, because OpenRouter reports each call's cost and its public
 * catalogue carries the list price for the estimate.
 *
 * The preprocess accepts the pre-2026-10-20 shape — a bare model id string —
 * and reads it as an Anthropic model, so an installation that saved one is
 * not reset to the default with a warning on every request.
 */
export const briefingModelSchema = z.preprocess(
  (value) => (typeof value === 'string' ? { provider: 'anthropic', id: value } : value),
  z.discriminatedUnion('provider', [
    z.strictObject({ provider: z.literal('anthropic'), id: z.enum(ANTHROPIC_MODEL_IDS) }),
    z.strictObject({ provider: z.literal('openrouter'), id: openRouterModelIdSchema }),
    z.strictObject({ provider: z.literal('mistral'), id: z.enum(MISTRAL_MODEL_IDS) }),
  ]),
)
export type BriefingModel = z.output<typeof briefingModelSchema>

/**
 * Models worth trying through OpenRouter for a briefing, as a starting point
 * for the picker — the field takes any id in the catalogue. Ids checked against
 * `GET /api/v1/models` on 2026-10-20; every one takes images, and `nativePdf`
 * says whether OpenRouter hands it the PDF itself (`file` input) or parses the
 * pages into text and up to eight images first (its `file-parser` plugin).
 * No prices here, on purpose (see `ai-usage.ts`).
 */
export const OPENROUTER_BRIEFING_SUGGESTIONS: readonly {
  id: string
  label: string
  nativePdf: boolean
}[] = [
  { id: 'google/gemini-4-argon', label: 'Google Gemini 4 Argon', nativePdf: true },
  { id: 'google/gemini-3.1-pro-preview', label: 'Google Gemini 3.1 Pro', nativePdf: true },
  { id: 'google/gemini-3.8-flash', label: 'Google Gemini 3.8 Flash', nativePdf: true },
  { id: 'openai/gpt-5.5', label: 'OpenAI GPT-5.5', nativePdf: true },
  { id: 'anthropic/claude-opus-5.5', label: 'Anthropic Claude Opus 5.5', nativePdf: true },
  { id: 'x-ai/grok-4.7', label: 'xAI Grok 4.7', nativePdf: true },
  { id: 'mistralai/mistral-medium-3-5', label: 'Mistral Medium 3.5', nativePdf: true },
  { id: 'qwen/qwen3.8-max-prime', label: 'Qwen 3.8 Max Prime', nativePdf: false },
  { id: 'qwen/qwen3.8-27b', label: 'Qwen 3.8 27B', nativePdf: false },
  { id: 'moonshotai/kimi-k3', label: 'Moonshot Kimi K3', nativePdf: false },
  { id: 'deepseek/deepseek-v4.1-flash-1017', label: 'DeepSeek V4.1 Flash', nativePdf: false },
]

/**
 * What a briefing's row or the setting shows as its model — never a price.
 * The route is named in brackets, not after a "·": the briefing page already
 * separates its fields with one, and "Claude Opus 5.5 (über OpenRouter)" is
 * the one place the same model's two routes are told apart.
 */
export function briefingModelLabel(model: { provider: BriefingProviderId; id: string }): string {
  if (model.provider !== 'openrouter') {
    return isPricedModelId(model.id) ? MODEL_LABELS[model.id] : model.id
  }
  const known = OPENROUTER_BRIEFING_SUGGESTIONS.find((entry) => entry.id === model.id)
  return `${known?.label ?? model.id} (über OpenRouter)`
}

/**
 * OpenRouter's catalogue, as the admin picker sees it: ids, names and what
 * each model can take. Served by `GET /settings/briefing-models` (admin-only),
 * read live from OpenRouter and cached for a while server-side. No prices and
 * no token figures — the same rule as every other response.
 */
export const openRouterCatalogueEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Accepts a PDF as a file; otherwise OpenRouter parses it to text first. */
  nativePdf: z.boolean(),
  /** Takes images — a parsed PDF's page images reach the model only then. */
  images: z.boolean(),
  /** The JSON schema can be enforced; otherwise the prompt alone asks for it. */
  structuredOutputs: z.boolean(),
  contextLength: z.number().int().nonnegative().nullable(),
})
export type OpenRouterCatalogueEntry = z.infer<typeof openRouterCatalogueEntrySchema>

export const openRouterCatalogueSchema = z.object({
  models: z.array(openRouterCatalogueEntrySchema),
  /** When OpenRouter was last asked; the list is cached server-side. */
  fetchedAt: z.iso.datetime(),
})
export type OpenRouterCatalogue = z.infer<typeof openRouterCatalogueSchema>
